import { useDataChainId } from '@ui/hooks/datachain';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { getSwapFacet } from '@ui/services/contract/contract-instances/getSwapFacet';
import { Token } from '@ui/types/token';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { useCallback, useMemo } from 'react';
import { Address, parseUnits } from 'viem';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';

type RedeemParams = {
  inputToken: Token;
  outputToken: Token;
  inputAmount: string;
  minOutputAmount: string;
  adapter: Address;
  adapterData: `0x${string}`;
  receiver: Address;
};

export const useFastRedeem = (params?: TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { data: walletClient } = useWalletClient({ chainId });
  const { address } = useAccount();

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccess: params?.onSuccess,
    onError: params?.onError,
    onSettled: params?.onSettled,
    onReceiptSettled: params?.onReceiptSettled,
  });

  const { isSimulating, simulate } = useSimulateMutation();

  const write = useCallback(async ({
    inputToken,
    outputToken,
    inputAmount,
  }: RedeemParams) => {
    if (!publicClient || !walletClient) {
      console.warn('Wallet not connected');
      return;
    }

    const swapFacet = getSwapFacet({
      chainId,
      publicClient: publicClient as any,
      walletClient,
    });

    const amountIn = parseUnits(inputAmount, inputToken.decimals);
    const reserveToken = outputToken.address as Address;

    const args = [
      reserveToken,
      amountIn,
    ] as const;

    const { isSuccess } = await simulate(async () => {
      await swapFacet.estimateGas.fastRedeemLVMON(args as any, {
        account: address,
      });
    });

    if (!isSuccess) {
        console.error("Simulation failed");
        return;
    }

    return writeContract({
      abi: swapFacet.abi,
      address: swapFacet.address,
      functionName: 'fastRedeemLVMON',
      args: args,
    });
  }, [chainId, publicClient, walletClient, simulate, writeContract, address]);

  return useMemo(() => {
    return {
      data,
      mutate: write,
      isPending: isSimulating || isPending,
    };
  }, [data, isPending, isSimulating, write]);
};
